/**
 * screens/modals/ApprovalRuleModal
 * ──────────────────────────────────────────────────────────────────────────
 * One condition, one or two approvers.
 *
 * The trigger CHANGES what the second field asks for, because the five triggers
 * compare against different things: an amount, a category, a holder type, an
 * instrument, or nothing at all (`noReceipt` is a fact, not a comparison).
 *
 * TWO APPROVERS MEANS BOTH. The modal says so on the field, because a
 * two-name list reads naturally as "either of these" and the engine means the
 * opposite — the item stays pending until two different people have approved.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Chip, ChipRow, Input, Modal } from '../../components';
import { neutral } from '../../theme/colors';
import { sans, size as fontSize, leading } from '../../theme/typography';
import { parseMajorToMinor } from '../../lib/money';
import { sentenceCase } from '../../lib/format';
import { createApprovalRule } from '../../services/config/config.service';
import { approversSentence, RULE_FIELDS, RULE_FIELD_LABEL, type RuleField } from '../../schemas/config';
import { useConfig } from '../../context/ConfigContext';
import { useShell } from '../../context/ShellContext';
import { useModalSubmit } from './useModalSubmit';

/** At most two approvers — the engine supports one or two, not a chain. */
const MAX_APPROVERS = 2;

export function ApprovalRuleModal() {
  const { closeModal } = useShell();
  const { optionsByKind, activeCategories, policy } = useConfig();

  const [field, setField] = useState<RuleField>('amount');
  const [amountText, setAmountText] = useState('');
  const [valueText, setValueText] = useState<string | null>(null);
  const [approvers, setApprovers] = useState<string[]>([]);

  const currency = policy?.base_currency ?? null;

  /** The second field's options, per trigger. */
  const valueOptions = useMemo(() => {
    switch (field) {
      case 'category':
        return activeCategories.map((category) => ({ value: category.id, label: category.code }));
      case 'holderType':
        return optionsByKind.holderType
          .filter((row) => row.is_active)
          .map((row) => ({ value: row.value, label: row.display_name || sentenceCase(row.value) }));
      case 'instrument':
        return optionsByKind.instrument
          .filter((row) => row.is_active)
          .map((row) => ({ value: row.value, label: row.display_name || sentenceCase(row.value) }));
      default:
        return [];
    }
  }, [field, activeCategories, optionsByKind]);

  const approverOptions = useMemo(
    () => optionsByKind.approver.filter((row) => row.is_active),
    [optionsByKind],
  );

  /** Approvers are multi-select up to two, so this is a toggle, not a pick. */
  const toggleApprover = (name: string) => {
    setApprovers((current) => {
      if (current.includes(name)) return current.filter((n) => n !== name);
      if (current.length >= MAX_APPROVERS) return current;
      return [...current, name];
    });
  };

  const { submitting, run } = useModalSubmit({
    validate: () => {
      if (field === 'amount') {
        if (!currency) return 'Set a base currency before adding an amount rule.';
        if (parseMajorToMinor(amountText, currency) === null) return 'Give the rule an amount.';
      } else if (field !== 'noReceipt' && !valueText) {
        return 'Pick what the rule applies to.';
      }
      if (approvers.length === 0) return 'Name at least one approver.';
      return null;
    },
    submit: () =>
      createApprovalRule({
        on_field: field,
        ...(field === 'amount' && currency
          ? { value_minor: parseMajorToMinor(amountText, currency)! }
          : {}),
        ...(field !== 'amount' && field !== 'noReceipt' && valueText
          ? { value_text: valueText }
          : {}),
        approvers,
      }),
    title: 'Rule added',
    fallback: 'It applies to the next spend.',
    tone: 'plain',
  });

  return (
    <Modal
      visible
      title="Add an approval rule"
      hint="One condition, one or two approvers."
      onClose={closeModal}
      primaryLabel="Add rule"
      onPrimary={run}
      primaryLoading={submitting}
    >
      <ChipRow
        label="Hold a spend when"
        required
        options={RULE_FIELDS.map((value) => ({
          value,
          label:
            value === 'amount'
              ? 'Amount'
              : value === 'noReceipt'
                ? 'No receipt'
                : sentenceCase(value.replace(/([A-Z])/g, ' $1')),
        }))}
        value={field}
        onChange={(next) => {
          setField(next);
          // The value field asks for something different now, so a stale answer
          // would silently apply to the wrong trigger.
          setValueText(null);
          setAmountText('');
        }}
      />

      {field === 'amount' && (
        <Input
          label={RULE_FIELD_LABEL.amount}
          required
          value={amountText}
          onChangeText={setAmountText}
          placeholder="0.00"
          prefix={currency ?? undefined}
          numeric
        />
      )}

      {(field === 'category' || field === 'holderType' || field === 'instrument') && (
        <ChipRow
          label={RULE_FIELD_LABEL[field]}
          required
          options={valueOptions}
          value={valueText}
          onChange={setValueText}
        />
      )}

      {field === 'noReceipt' && (
        <View style={styles.note}>
          <Text style={styles.noteText}>
            Any spend recorded without an evidence reference will be held for these approvers.
          </Text>
        </View>
      )}

      {/* Approvers are the one multi-select in the product, capped at two. */}
      <View style={styles.field}>
        <Text style={styles.label}>
          Approvers
          <Text style={styles.required}> · required</Text>
        </Text>
        <Text style={styles.hint}>
          Choosing two means BOTH must approve — the item stays pending until two different people
          have acted.
        </Text>
        <View style={styles.chips}>
          {approverOptions.map((option) => (
            <Chip
              key={option.id}
              label={option.display_name || option.value}
              selected={approvers.includes(option.value)}
              disabled={!approvers.includes(option.value) && approvers.length >= MAX_APPROVERS}
              onPress={() => toggleApprover(option.value)}
            />
          ))}
        </View>
        {approvers.length > 0 && (
          <Text style={styles.summary}>{approversSentence(approvers)}</Text>
        )}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  field: { gap: 7 },
  label: { ...sans('500'), fontSize: fontSize.label, color: neutral.ink2 },
  required: { ...sans('400'), color: neutral.grey400 },
  hint: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: neutral.grey600,
  },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  summary: { ...sans('500'), fontSize: fontSize.meta, color: neutral.ink },
  note: { paddingVertical: 2 },
  noteText: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: neutral.grey600,
  },
});

export default ApprovalRuleModal;
